import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button } from 'react-bootstrap';
import {
  APP_VERSION,
  formatVersion,
  getPageVersionForPath,
} from '../versionInfo';
import { SHOPPING_CART_STORAGE_KEY } from '../storageRecovery';
import { markAppStartupComplete, reportAppStartupError } from '../startupTelemetry';

type AppRecoveryBoundaryProps = {
  children: ReactNode;
};

type AppRecoveryBoundaryState = {
  error: Error | null;
};

/**
 * AppRecoveryBoundary
 *
 * Wraps the complete application root and replaces unexpected React render
 * failures with a recoverable Bootstrap screen. It receives the App tree,
 * depends on version metadata, startup telemetry, browser location/storage,
 * and performs recovery side effects: reporting, reload, and saved-cart reset.
 */
class AppRecoveryBoundary extends Component<AppRecoveryBoundaryProps, AppRecoveryBoundaryState> {
  state: AppRecoveryBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): AppRecoveryBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Unhandled React render error', error, errorInfo);
    reportAppStartupError('react-render-error', error, errorInfo.componentStack ?? undefined);
    markAppStartupComplete();
  }

  private reloadPage = (): void => {
    window.location.reload();
  };

  private clearSavedCartAndReload = (): void => {
    window.localStorage.removeItem(SHOPPING_CART_STORAGE_KEY);
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const currentRoute = `${window.location.pathname}${window.location.search}`;
    const pageVersion = getPageVersionForPath(window.location.pathname);

    return (
      <main className="bg-light min-vh-100 d-flex align-items-center justify-content-center p-3" data-testid="app-recovery-screen">
        <section className="bg-white border rounded-4 shadow-sm p-4 w-100" style={{ maxWidth: '520px' }}>
          <h1 className="h4 fw-bold mb-3">We could not show this page</h1>
          <Alert variant="warning" className="small mb-4">
            Reload the page. If the problem repeats, clear the saved cart and reload.
          </Alert>

          <dl className="row small mb-4">
            <dt className="col-5 text-secondary">App version</dt>
            <dd className="col-7" data-testid="app-recovery-app-version">{formatVersion(APP_VERSION)}</dd>
            <dt className="col-5 text-secondary">Page version</dt>
            <dd className="col-7" data-testid="app-recovery-page-version">{formatVersion(pageVersion)}</dd>
            <dt className="col-5 text-secondary">Current route</dt>
            <dd className="col-7 text-break" data-testid="app-recovery-route">{currentRoute}</dd>
          </dl>

          <div className="d-flex flex-column gap-2">
            <Button variant="success" className="rounded-pill fw-semibold" onClick={this.reloadPage} data-testid="app-recovery-reload">
              Reload page
            </Button>
            <Button variant="outline-success" className="rounded-pill fw-semibold" onClick={this.clearSavedCartAndReload} data-testid="app-recovery-clear-cart">
              Clear saved cart and reload
            </Button>
          </div>
        </section>
      </main>
    );
  }
}

export default AppRecoveryBoundary;
